(() => {
  const GlobalAudioContext = {
    activeElement: null,
  };

  const APILoader = {
    yt: { loading: false, callbacks: [] },
    vimeo: { loading: false, callbacks: [] },
    loadYouTube(cb) {
      if (window.YT && window.YT.Player) return cb();
      this.yt.callbacks.push(cb);
      if (!this.yt.loading) {
        this.yt.loading = true;
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        window.onYouTubeIframeAPIReady = () => {
          this.yt.callbacks.forEach((c) => c());
        };
        document.head.appendChild(script);
      }
    },
    loadVimeo(cb) {
      if (window.Vimeo && window.Vimeo.Player) return cb();
      this.vimeo.callbacks.push(cb);
      if (!this.vimeo.loading) {
        this.vimeo.loading = true;
        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.onload = () => {
          this.vimeo.callbacks.forEach((c) => c());
        };
        document.head.appendChild(script);
      }
    },
  };

  class VideoIntersectionInsert extends HTMLElement {
    constructor() {
      super();
      this._observer = null;
      this._isPlaying = false;
      this._loaded = false;
      this._ready = false;
      this._pendingPlay = false;

      this._provider = this.dataset.provider || 'html5';
      this._videoId = this.dataset.videoId;
      this._muted = this.dataset.muted === 'true';
      this._controls = this.dataset.controls === 'true';
      this._player = null;
      this._isModal = this.dataset.isModal === 'true';
      this._pausedOnLoad = this.dataset.pausedOnLoad === 'true';
    }

    connectedCallback() {
      if (!this._videoId && !this.dataset.src) return;

      this._observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const isVisible = entry.isIntersecting;

          if (isVisible && !this._loaded) {
            this.loadMedia();
          }

          if (this._pausedOnLoad) {
            if (!isVisible && this._isPlaying) {
              this.pauseMedia();
            }
          } else {
            if (isVisible && this._loaded && !this._isPlaying) {
              if (this._muted || this._isModal) {
                this.playMedia();
              }
            } else if (!isVisible && this._isPlaying) {
              this.handleViewportExit();
            }
          }
        },
        {
          root: null,
          rootMargin: '200px',
          threshold: [0],
        },
      );

      this._observer.observe(this);
    }

    handleViewportExit() {
      this.pauseMedia();
      if (!this._muted) {
        this.muteMedia();
      }
    }

    loadMedia() {
      this.innerHTML = '';
      this._loaded = true;

      if (this._provider === 'youtube') {
        const container = document.createElement('div');
        container.className = 'w-full h-full';
        if (!this._controls && this._muted) container.style.pointerEvents = 'none';
        this.appendChild(container);
        APILoader.loadYouTube(() => this.initYouTube(container));
      } else if (this._provider === 'vimeo') {
        const container = document.createElement('div');
        container.className = 'w-full h-full';
        if (!this._controls && this._muted) container.style.pointerEvents = 'none';
        this.appendChild(container);
        APILoader.loadVimeo(() => this.initVimeo(container));
      } else {
        this.loadHTML5();
      }
    }

    loadHTML5() {
      if (this.dataset.src) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.dataset.src;
        this._player = tempDiv.querySelector('video');
        this.appendChild(this._player);
      } else {
        this._player = document.createElement('video');
        this._player.src = this._videoId;
        this._player.className = this.dataset.class || '';
        this._player.loop = true;
        this._player.playsInline = true;
        if (this._muted) this._player.muted = true;
        if (this._controls) {
          this._player.controls = true;
        }
        this.appendChild(this._player);
      }

      if (!this._controls) {
        this._player.removeAttribute('controls');
        if (this._muted) {
          this._player.style.pointerEvents = 'none';
        } else {
          this._player.style.cursor = 'pointer';
          this._player.addEventListener('click', () => {
            if (this._isPlaying) {
              this.pauseMedia();
            } else {
              this.playMedia();
            }
          });
        }
      }

      if (this._muted) {
        if (this._player.readyState >= 1) {
          this.playMedia();
        } else {
          this._player.addEventListener(
            'loadedmetadata',
            () => {
              this.playMedia();
            },
            { once: true },
          );
        }
      }

      this._player.addEventListener('play', () => {
        this._isPlaying = true;
      });
      this._player.addEventListener('pause', () => {
        this._isPlaying = false;
      });
    }

    initYouTube(container) {
      const autoplayFlag = this._muted && !this._pausedOnLoad ? 1 : 0;

      // 动态判断：有控制条就给完整原生体验，无控制条则极限精简
      const playerVars = {
        autoplay: autoplayFlag,
        controls: this._controls ? 1 : 0,
        mute: this._muted ? 1 : 0,
        loop: 1,
        playlist: this._videoId,
        playsinline: 1,
        rel: 0,
      };

      if (!this._controls) {
        playerVars.modestbranding = 1;
        playerVars.disablekb = 1;
        playerVars.iv_load_policy = 3;
      }

      this._player = new YT.Player(container, {
        videoId: this._videoId,
        width: '100%', // 强制满宽满高，修复 UI 控制栏被切除的 Bug
        height: '100%',
        playerVars: playerVars,
        events: {
          onReady: () => {
            this._ready = true;
            if (this._pendingPlay) {
              this._pendingPlay = false;
              this.playMedia();
            } else if (!this._pausedOnLoad && (this._muted || this._isModal)) {
              this.playMedia();
            }
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) {
              this._isPlaying = true;
            } else if (e.data === YT.PlayerState.PAUSED) {
              this._isPlaying = false;
            }
          },
        },
      });
    }

    initVimeo(container) {
      this._player = new Vimeo.Player(container, {
        id: this._videoId,
        width: '100%',
        height: '100%',
        autoplay: this._muted && !this._pausedOnLoad,
        controls: this._controls,
        muted: this._muted,
        loop: true,
        background: !this._controls && this._muted && !this._pausedOnLoad,
        // 动态判断：只有 controls: true 时才显示原生标题和头像信息
        title: this._controls,
        byline: this._controls,
        portrait: this._controls,
        dnt: true,
      });

      this._player.ready().then(() => {
        this._ready = true;
        if (this._pendingPlay) {
          this._pendingPlay = false;
          this.playMedia();
        } else if (!this._pausedOnLoad && (this._muted || this._isModal)) {
          this.playMedia();
        }
      });

      this._player.on('play', () => {
        this._isPlaying = true;
      });
      this._player.on('pause', () => {
        this._isPlaying = false;
      });
    }

    playMedia() {
      if ((this._provider === 'youtube' || this._provider === 'vimeo') && !this._ready) {
        if (!this._loaded) this.loadMedia();
        this._pendingPlay = true;
        return;
      }

      if (!this._muted) {
        if (GlobalAudioContext.activeElement && GlobalAudioContext.activeElement !== this) {
          GlobalAudioContext.activeElement.muteMedia();
        }
        GlobalAudioContext.activeElement = this;
      }

      if (this._provider === 'html5' && this._player) {
        const playPromise = this._player.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              this._isPlaying = true;
              if (!this._muted) this._player.muted = false;
            })
            .catch(() => {
              this._isPlaying = false;
            });
        }
      } else if (this._provider === 'youtube' && this._player?.playVideo) {
        this._player.playVideo();
        if (!this._muted) this._player.unMute();
        this._isPlaying = true;
      } else if (this._provider === 'vimeo' && this._player?.play) {
        this._player.play();
        if (!this._muted) this._player.setVolume(1);
        this._isPlaying = true;
      }
    }

    pauseMedia() {
      if (this._provider === 'html5' && this._player) {
        this._player.pause();
      } else if (this._provider === 'youtube' && this._player?.pauseVideo) {
        this._player.pauseVideo();
      } else if (this._provider === 'vimeo' && this._player?.pause) {
        this._player.pause();
      }
      this._isPlaying = false;
    }

    muteMedia() {
      if (this._provider === 'html5' && this._player) {
        this._player.muted = true;
      } else if (this._provider === 'youtube' && this._player?.mute) {
        this._player.mute();
      } else if (this._provider === 'vimeo' && this._player?.setVolume) {
        this._player.setVolume(0);
      }

      if (GlobalAudioContext.activeElement === this) {
        GlobalAudioContext.activeElement = null;
      }
    }

    resetMedia() {
      if (this._provider === 'html5' && this._player) {
        this._player.currentTime = 0;
      } else if (this._provider === 'youtube' && this._player?.seekTo) {
        this._player.seekTo(0, true);
      } else if (this._provider === 'vimeo' && this._player?.setCurrentTime) {
        this._player.setCurrentTime(0);
      }
    }

    disconnectedCallback() {
      this._observer?.disconnect();
    }
  }

  if (!customElements.get('video-intersection-insert')) {
    customElements.define('video-intersection-insert', VideoIntersectionInsert);
  }

  // node_modules/ftdomdelegate/main.js
  function Delegate(root) {
    this.listenerMap = [{}, {}];
    if (root) {
      this.root(root);
    }
    this.handle = Delegate.prototype.handle.bind(this);
    this._removedListeners = [];
  }
  Delegate.prototype.root = function (root) {
    const listenerMap = this.listenerMap;
    let eventType;
    if (this.rootElement) {
      for (eventType in listenerMap[1]) {
        if (listenerMap[1].hasOwnProperty(eventType)) {
          this.rootElement.removeEventListener(eventType, this.handle, true);
        }
      }
      for (eventType in listenerMap[0]) {
        if (listenerMap[0].hasOwnProperty(eventType)) {
          this.rootElement.removeEventListener(eventType, this.handle, false);
        }
      }
    }
    if (!root || !root.addEventListener) {
      if (this.rootElement) {
        delete this.rootElement;
      }
      return this;
    }
    this.rootElement = root;
    for (eventType in listenerMap[1]) {
      if (listenerMap[1].hasOwnProperty(eventType)) {
        this.rootElement.addEventListener(eventType, this.handle, true);
      }
    }
    for (eventType in listenerMap[0]) {
      if (listenerMap[0].hasOwnProperty(eventType)) {
        this.rootElement.addEventListener(eventType, this.handle, false);
      }
    }
    return this;
  };
  Delegate.prototype.captureForType = function (eventType) {
    return ['blur', 'error', 'focus', 'load', 'resize', 'scroll'].indexOf(eventType) !== -1;
  };
  Delegate.prototype.on = function (eventType, selector, handler, useCapture) {
    let root;
    let listenerMap;
    let matcher;
    let matcherParam;
    if (!eventType) {
      throw new TypeError('Invalid event type: ' + eventType);
    }
    if (typeof selector === 'function') {
      useCapture = handler;
      handler = selector;
      selector = null;
    }
    if (useCapture === void 0) {
      useCapture = this.captureForType(eventType);
    }
    if (typeof handler !== 'function') {
      throw new TypeError('Handler must be a type of Function');
    }
    root = this.rootElement;
    listenerMap = this.listenerMap[useCapture ? 1 : 0];
    if (!listenerMap[eventType]) {
      if (root) {
        root.addEventListener(eventType, this.handle, useCapture);
      }
      listenerMap[eventType] = [];
    }
    if (!selector) {
      matcherParam = null;
      matcher = matchesRoot.bind(this);
    } else if (/^[a-z]+$/i.test(selector)) {
      matcherParam = selector;
      matcher = matchesTag;
    } else if (/^#[a-z0-9\-_]+$/i.test(selector)) {
      matcherParam = selector.slice(1);
      matcher = matchesId;
    } else {
      matcherParam = selector;
      matcher = Element.prototype.matches;
    }
    listenerMap[eventType].push({
      selector,
      handler,
      matcher,
      matcherParam,
    });
    return this;
  };
  Delegate.prototype.off = function (eventType, selector, handler, useCapture) {
    let i;
    let listener;
    let listenerMap;
    let listenerList;
    let singleEventType;
    if (typeof selector === 'function') {
      useCapture = handler;
      handler = selector;
      selector = null;
    }
    if (useCapture === void 0) {
      this.off(eventType, selector, handler, true);
      this.off(eventType, selector, handler, false);
      return this;
    }
    listenerMap = this.listenerMap[useCapture ? 1 : 0];
    if (!eventType) {
      for (singleEventType in listenerMap) {
        if (listenerMap.hasOwnProperty(singleEventType)) {
          this.off(singleEventType, selector, handler);
        }
      }
      return this;
    }
    listenerList = listenerMap[eventType];
    if (!listenerList || !listenerList.length) {
      return this;
    }
    for (i = listenerList.length - 1; i >= 0; i--) {
      listener = listenerList[i];
      if ((!selector || selector === listener.selector) && (!handler || handler === listener.handler)) {
        this._removedListeners.push(listener);
        listenerList.splice(i, 1);
      }
    }
    if (!listenerList.length) {
      delete listenerMap[eventType];
      if (this.rootElement) {
        this.rootElement.removeEventListener(eventType, this.handle, useCapture);
      }
    }
    return this;
  };
  Delegate.prototype.handle = function (event) {
    let i;
    let l;
    const type = event.type;
    let root;
    let phase;
    let listener;
    let returned;
    let listenerList = [];
    let target;
    const eventIgnore = 'ftLabsDelegateIgnore';
    if (event[eventIgnore] === true) {
      return;
    }
    target = event.target;
    if (target.nodeType === 3) {
      target = target.parentNode;
    }
    if (target.correspondingUseElement) {
      target = target.correspondingUseElement;
    }
    root = this.rootElement;
    phase = event.eventPhase || (event.target !== event.currentTarget ? 3 : 2);
    switch (phase) {
      case 1:
        listenerList = this.listenerMap[1][type];
        break;
      case 2:
        if (this.listenerMap[0] && this.listenerMap[0][type]) {
          listenerList = listenerList.concat(this.listenerMap[0][type]);
        }
        if (this.listenerMap[1] && this.listenerMap[1][type]) {
          listenerList = listenerList.concat(this.listenerMap[1][type]);
        }
        break;
      case 3:
        listenerList = this.listenerMap[0][type];
        break;
    }
    let toFire = [];
    l = listenerList.length;
    while (target && l) {
      for (i = 0; i < l; i++) {
        listener = listenerList[i];
        if (!listener) {
          break;
        }
        if (
          target.tagName &&
          ['button', 'input', 'select', 'textarea'].indexOf(target.tagName.toLowerCase()) > -1 &&
          target.hasAttribute('disabled')
        ) {
          toFire = [];
        } else if (listener.matcher.call(target, listener.matcherParam, target)) {
          toFire.push([event, target, listener]);
        }
      }
      if (target === root) {
        break;
      }
      l = listenerList.length;
      target = target.parentElement || target.parentNode;
      if (target instanceof HTMLDocument) {
        break;
      }
    }
    let ret;
    for (i = 0; i < toFire.length; i++) {
      if (this._removedListeners.indexOf(toFire[i][2]) > -1) {
        continue;
      }
      returned = this.fire.apply(this, toFire[i]);
      if (returned === false) {
        toFire[i][0][eventIgnore] = true;
        toFire[i][0].preventDefault();
        ret = false;
        break;
      }
    }
    return ret;
  };
  Delegate.prototype.fire = function (event, target, listener) {
    return listener.handler.call(target, event, target);
  };
  const main_delegate = Delegate;
  function matchesTag(tagName, element) {
    return tagName.toLowerCase() === element.tagName.toLowerCase();
  }
  function matchesRoot(selector, element) {
    if (this.rootElement === window) {
      return element === document || element === document.documentElement || element === window;
    }
    return this.rootElement === element;
  }
  function matchesId(id, element) {
    return id === element.id;
  }
  Delegate.prototype.destroy = function () {
    this.off();
    this.root();
  };

  var MediaFeatures = class {
    static prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    static supportsHover() {
      return window.matchMedia('(pointer: fine)').matches;
    }
  };
  var CustomAnimation = class {
    constructor(effect) {
      this._effect = effect;
      this._playState = 'idle';
      this._finished = Promise.resolve();
    }
    get finished() {
      return this._finished;
    }
    get animationEffects() {
      return this._effect instanceof CustomKeyframeEffect ? [this._effect] : this._effect.animationEffects;
    }
    cancel() {
      this.animationEffects.forEach((animationEffect) => animationEffect.cancel());
    }
    finish() {
      this.animationEffects.forEach((animationEffect) => animationEffect.finish());
    }
    play() {
      this._playState = 'running';
      this._effect.play();
      this._finished = this._effect.finished;
      this._finished.then(
        () => {
          this._playState = 'finished';
        },
        (rejection) => {
          this._playState = 'idle';
        },
      );
    }
  };
  var CustomKeyframeEffect = class {
    constructor(target, keyframes, options = {}) {
      if (!target) {
        return;
      }
      if ('Animation' in window) {
        this._animation = new Animation(new KeyframeEffect(target, keyframes, options));
      } else {
        options['fill'] = 'forwards';
        this._animation = target.animate(keyframes, options);
        this._animation.pause();
      }
      this._animation.addEventListener('finish', () => {
        target.style.opacity = keyframes.hasOwnProperty('opacity')
          ? keyframes['opacity'][keyframes['opacity'].length - 1]
          : null;
        target.style.visibility = keyframes.hasOwnProperty('visibility')
          ? keyframes['visibility'][keyframes['visibility'].length - 1]
          : null;
      });
    }
    get finished() {
      if (!this._animation) {
        return Promise.resolve();
      }
      return this._animation.finished
        ? this._animation.finished
        : new Promise((resolve) => (this._animation.onfinish = resolve));
    }
    play() {
      if (this._animation) {
        this._animation.startTime = null;
        this._animation.play();
      }
    }
    cancel() {
      if (this._animation) {
        this._animation.cancel();
      }
    }
    finish() {
      if (this._animation) {
        this._animation.finish();
      }
    }
  };
  var GroupEffect = class {
    constructor(childrenEffects) {
      this._childrenEffects = childrenEffects;
      this._finished = Promise.resolve();
    }
    get finished() {
      return this._finished;
    }
    get animationEffects() {
      return this._childrenEffects.flatMap((effect) => {
        return effect instanceof CustomKeyframeEffect ? effect : effect.animationEffects;
      });
    }
  };
  var ParallelEffect = class extends GroupEffect {
    play() {
      const promises = [];
      for (const effect of this._childrenEffects) {
        effect.play();
        promises.push(effect.finished);
      }
      this._finished = Promise.all(promises);
    }
  };

  var CustomHTMLElement = class extends HTMLElement {
    constructor() {
      super();
      this._hasSectionReloaded = false;
      if (Shopify.designMode) {
        this.rootDelegate.on('shopify:section:select', (event) => {
          const parentSection = this.closest('.shopify-section');
          if (event.target === parentSection && event.detail.load) {
            this._hasSectionReloaded = true;
          }
        });
      }
    }
    get rootDelegate() {
      return (this._rootDelegate = this._rootDelegate || new main_delegate(document.documentElement));
    }
    get delegate() {
      return (this._delegate = this._delegate || new main_delegate(this));
    }
    untilVisible(intersectionObserverOptions = { rootMargin: '30px 0px', threshold: 0 }) {
      const onBecameVisible = () => {
        this.classList.add('became-visible');
        this.style.opacity = '1';
      };
      return new Promise((resolve) => {
        if (window.IntersectionObserver) {
          this.intersectionObserver = new IntersectionObserver((event) => {
            if (event[0].isIntersecting) {
              this.intersectionObserver.disconnect();
              requestAnimationFrame(() => {
                resolve();
                onBecameVisible();
              });
            }
          }, intersectionObserverOptions);
          this.intersectionObserver.observe(this);
        } else {
          resolve();
          onBecameVisible();
        }
      });
    }
    disconnectedCallback() {
      this.delegate.destroy();
      this.rootDelegate.destroy();
      delete this._delegate;
      delete this._rootDelegate;
    }
  };

  // js/custom-element/section/blog/article-list.js
  var ArticleList = class extends CustomHTMLElement {
    async connectedCallback() {
      this.articleItems = Array.from(this.querySelectorAll('.article-layout-card'));
      if (this.staggerApparition) {
        await this.untilVisible({
          threshold: this.clientHeight > 0 ? Math.min(50 / this.clientHeight, 1) : 0,
        });
        const animation = new CustomAnimation(
          new ParallelEffect(
            this.articleItems.map((item, index) => {
              return new CustomKeyframeEffect(
                item,
                {
                  opacity: [0, 1],
                  transform: [
                    `translateY(${MediaFeatures.prefersReducedMotion() ? 0 : window.innerWidth < 768 ? 35 : 60}px)`,
                    'translateY(0)',
                  ],
                },
                {
                  duration: 600,
                  delay: MediaFeatures.prefersReducedMotion()
                    ? 0
                    : 100 * index - Math.min(3 * index * index, 100 * index),
                  easing: 'ease',
                },
              );
            }),
          ),
        );
        this._hasSectionReloaded ? animation.finish() : animation.play();
      }
    }
    get staggerApparition() {
      return this.hasAttribute('stagger-apparition');
    }
  };
  window.customElements.define('article-list', ArticleList);

  // js/custom-element/behavior/page-pagination.js
  var PagePagination = class extends CustomHTMLElement {
    connectedCallback() {
      if (this.hasAttribute('ajax')) {
        this.delegate.on('click', 'a', this._onLinkClicked.bind(this));
      }
    }
    _onLinkClicked(event, target) {
      event.preventDefault();
      const url = new URL(window.location.href);
      url.searchParams.set('page', target.getAttribute('data-page'));
      history.pushState(null, '', url.toString());
      document.dispatchEvent(
        new CustomEvent('blog:page-changed', {
          detail: { url: url.toString() },
        }),
      );
    }
  };
  window.customElements.define('page-pagination', PagePagination);

  class BackToTop extends HTMLElement {
    constructor() {
      super();
      this.scrollThreshold = 600;
      this.scrollIdleDelay = 1000;
      this.backTopBtn = null;
      this.backTopTimer = null;
      this.onScroll = this.handleScroll.bind(this);
      this.onClick = this.scrollToTop.bind(this);
    }

    connectedCallback() {
      const iconTemplate = this.querySelector('[data-back-top-icon]');
      this._iconHtml = iconTemplate ? iconTemplate.innerHTML.trim() : '';    
      this.render();
      this.backTopBtn = this.querySelector('.back-top');

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.backTopBtn.addEventListener('click', this.onClick);
      this.updateVisibility();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.backTopBtn.removeEventListener('click', this.onClick);
      clearTimeout(this.backTopTimer);
    }

    getScrollTop() {
      return document.documentElement.scrollTop || document.body.scrollTop;
    }

    shouldShow() {
      return this.getScrollTop() > this.scrollThreshold;
    }

    updateVisibility() {
      this.backTopBtn.classList.toggle('show', this.shouldShow());
    }

    handleScroll() {
      this.backTopBtn.classList.remove('show');

      clearTimeout(this.backTopTimer);
      this.backTopTimer = setTimeout(() => {
        this.updateVisibility();
      }, this.scrollIdleDelay);
    }

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth', // 平滑滚动
      });
    }

    render() {
      this.innerHTML = `
        <span class="back-top" id="backtop">
          ${this.renderIcon()}
        </span>
      `;
    }

    renderIcon() {
      return this._iconHtml || '';
    }
  }

  customElements.define('back-to-top', BackToTop);
  // 锚点平滑滚动
  function scrollToAnchorTarget(target, { ignoreHeaderOffset = false } = {}) {
    let targetTop = target.getBoundingClientRect().top + window.scrollY;

    if (!ignoreHeaderOffset) {
      const stickyHeader = document.querySelector('.header');
      const headerOffset = stickyHeader ? stickyHeader.offsetHeight : 0;
      targetTop -= headerOffset + 60;
    }

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: 'smooth',
    });
  }

  window.scrollToAnchorTarget = scrollToAnchorTarget;

  function initAnchorScrollLinks() {
    document.querySelectorAll('a[data-anchor-id]').forEach((link) => {
      if (link.dataset.anchorScrollBound === 'true') return;

      link.dataset.anchorScrollBound = 'true';
      link.addEventListener('click', (e) => {
        const targetId = link.dataset.anchorId;
        if (!targetId) return;

        e.preventDefault();

        const target = document.querySelector(`[data-anchor-target-id='${targetId}']`);
        if (!target) return;

        scrollToAnchorTarget(target, {
          ignoreHeaderOffset: link.dataset.anchorIgnoreHeaderOffset === 'true',
        });
      });
    });
  }

  document.addEventListener('DOMContentLoaded', initAnchorScrollLinks);
  document.addEventListener('shopify:section:load', initAnchorScrollLinks);
})();
